import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { submissionsApi } from '../services/api';
import { DeckSubmission } from '../types';

const Decks: React.FC = () => {
  const [submissions, setSubmissions] = useState<DeckSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await submissionsApi.getAll();
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading submissions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Deck Submissions</h1>
          <Link 
            to="/submit-deck"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Submit New Deck
          </Link>
        </div>

        <div className="space-y-4">
          {submissions.map((submission) => (
            <div key={submission.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{submission.deck_name}</h3>
                  <p className="text-gray-600 mb-1">
                    Tournament: {submission.tournament?.name || 'Unknown'}
                  </p>
                  <p className="text-gray-600">
                    Total Cost: €{submission.total_cost.toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(submission.status)}`}>
                    {submission.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Submitted:</span>
                  <p className="text-gray-900">{new Date(submission.created_at).toLocaleString()}</p>
                </div>
                {submission.approved_at && (
                  <div>
                    <span className="font-medium text-gray-500">Reviewed:</span>
                    <p className="text-gray-900">{new Date(submission.approved_at).toLocaleString()}</p>
                  </div>
                )}
                {submission.rejection_reason && (
                  <div className="md:col-span-3">
                    <span className="font-medium text-gray-500">Rejection Reason:</span>
                    <p className="text-red-600">{submission.rejection_reason}</p>
                  </div>
                )}
              </div>

              {submission.validation_errors && submission.validation_errors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Validation Errors:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {submission.validation_errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 flex space-x-2">
                <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm">
                  View Details
                </button>
                {submission.status === 'DRAFT' && (
                  <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm">
                    Edit
                  </button>
                )}
                {submission.status === 'DRAFT' && (
                  <button className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-sm">
                    Submit for Review
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {submissions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No deck submissions found. Submit your first deck!</p>
            <Link 
              to="/submit-deck"
              className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit Deck
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Decks;